import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface ChartInfoButtonProps {
  onClick: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const ChartInfoButton: React.FC<ChartInfoButtonProps> = ({
  onClick,
  className = '',
  size = 'md',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    setIsAnimating(true);
    onClick();

    // Haptic feedback for mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }

    // Reset animation after completion
    setTimeout(() => setIsAnimating(false), 400);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    // Calculate tooltip position
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 40, // 40px above the button
        left: rect.left + rect.width / 2,
      });
    }
    // Show tooltip after a short delay
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sizeClasses = {
    sm: 'h-8 w-12',
    md: 'h-10 w-14',
    lg: 'h-12 w-16',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  return (
    <>
      {/* Tooltip Portal */}
      {showTooltip &&
        createPortal(
          <div
            className="fixed transform -translate-x-1/2 z-[99999] pointer-events-none"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            <div className="bg-gray-800 text-white text-sm px-3 py-2 rounded-md whitespace-nowrap shadow-md animate-fadeIn">
              مشاهده راهنمای نمودار
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800"></div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <button
        ref={buttonRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          relative inline-flex items-center justify-center rounded-full
          transition-all duration-300 transform
          ${sizeClasses[size]}
          bg-bg-secondary
          ${isHovered ? 'bg-bg-tertiary scale-110 shadow-lg' : ''}
          ${isAnimating ? 'scale-125 bg-primary/20' : ''}
          cursor-pointer hover:shadow-lg
          ${className}
        `}
        aria-label="اطلاعات نمودار"
      >
        {/* Info Icon with animation */}
        <Info
          size={iconSizes[size]}
          className={`
            text-text-secondary transition-all duration-300
            ${isHovered ? 'text-primary scale-110' : ''}
            ${isAnimating ? 'animate-pulse text-primary' : ''}
          `}
          style={{
            transform: isHovered ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'all 0.3s ease-in-out',
          }}
        />

        {/* Pulse animation on click */}
        {isAnimating && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-full h-full rounded-full bg-primary/30 animate-ping absolute"></div>
          </div>
        )}
      </button>
    </>
  );
};

export default ChartInfoButton;
